import { useCallback } from "react";
import {
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Progress,
  Text,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import useInactivityTimeout from "../../hooks/useInactivityTimeout";
import { useAppDispatch } from "../../redux/hooks";
import { logout } from "../../redux/modules/auth";
import api from "../../utils/apiClient";
import { getStoredAuthToken, goToPublicHome } from "../../utils/restoreAuthSession";

/**
 * Signs a member out after a spell of inactivity, giving them a visible countdown
 * to carry on first. Mounted once behind every signed-in page.
 */
const InactivityGuard = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const endSession = useCallback(
    async (timedOut: boolean) => {
      if (timedOut && getStoredAuthToken()) {
        try {
          // Sent while the token is still valid, so the member gets the email.
          await api.post("Session/InactivityTimeout", {
            data: { publicBaseUrl: window.location.origin, siteGroup: "widget" },
          });
        } catch {
          // Signing them out matters more than the courtesy email.
        }
      }
      dispatch(logout());
      if (timedOut) {
        navigate("/login?reason=inactivity");
        return;
      }
      goToPublicHome();
    },
    [dispatch, navigate]
  );

  const { isWarning, secondsLeft, staySignedIn, warningSeconds } =
    useInactivityTimeout({ onTimeout: () => endSession(true) });

  return (
    <Modal
      isOpen={isWarning}
      onClose={staySignedIn}
      isCentered
      closeOnOverlayClick={false}
      size={{ base: "sm", md: "md" }}
    >
      <ModalOverlay bg="white" />
      <ModalContent bg="white" mx={4}>
        <ModalHeader fontSize="lg" pb={1}>
          MITO Security
        </ModalHeader>
        <ModalBody>
          <Text color="black.100">
            We are timing you out for inactivity so your account stays
            protected.
          </Text>
          <Flex align="baseline" gap={2} mt={4}>
            <Text fontSize="xxl" fontWeight="700" color="var(--clr1)">
              {secondsLeft}
            </Text>
            <Text color="black.100">
              {secondsLeft === 1 ? "second" : "seconds"} left
            </Text>
          </Flex>
          <Progress
            mt={2}
            size="sm"
            borderRadius="4px"
            value={warningSeconds > 0 ? (secondsLeft / warningSeconds) * 100 : 0}
            sx={{ "& > div": { background: "var(--clr1)" } }}
          />
        </ModalBody>
        <ModalFooter
          gap={3}
          flexDirection={{ base: "column-reverse", sm: "row" }}
        >
          <Button
            variant="Solid_outline_primary"
            w={{ base: "100%", sm: "auto" }}
            onClick={() => endSession(false)}
          >
            Log Out Now
          </Button>
          <Button
            bg="var(--clr1)"
            color="#fff"
            _hover={{ bg: "var(--clr1)", opacity: 0.9 }}
            w={{ base: "100%", sm: "auto" }}
            onClick={staySignedIn}
          >
            Stay Signed In
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default InactivityGuard;
